import type { MessageEntity } from "@/indexedDB/types";

/**
 * The label the server substitutes at read time for a sender whose account no
 * longer exists (account deletion). Matches the backend's
 * `DELETED_SENDER_LABEL` and the participant string written into anonymised
 * DMs — one string, both places.
 */
export const DELETED_SENDER_LABEL = "Deleted User";

/**
 * Whether a message's sender is the deleted-user label.
 *
 * Prefers the `senderDeleted` wire flag (BE contract: the flag is set exactly
 * when `sender` is the label). The literal fallback covers rows that never
 * carry the flag: Rust-era anonymisation (public chats only), rows persisted
 * locally before the flag shipped, and DM participant strings. The fallback is
 * safe — the username regex (`/^[a-z0-9._-]+$/`) can never produce a value
 * with a space or uppercase letters, so no real account can collide with the
 * label.
 */
export function isDeletedSender(
  message: Pick<MessageEntity, "sender"> & { senderDeleted?: boolean },
): boolean {
  return (
    message.senderDeleted === true || message.sender === DELETED_SENDER_LABEL
  );
}

/**
 * String-only variant for call sites that hold just a username (avatar
 * rendering, DM chat names) where no wire flag exists.
 */
export function isDeletedUserLabel(name: string | null | undefined): boolean {
  return name === DELETED_SENDER_LABEL;
}
